/**
 * Workspace member roles matching backend WorkspaceRole enum.
 * Order indicates permission hierarchy (Admin > Editor > Viewer).
 *
 * Note: Ownership is tracked separately via isOwner flag, not through role.
 * An owner always has Admin role for permissions.
 */
export type WorkspaceRole = 'Admin' | 'Editor' | 'Viewer';

/** Array of all workspace roles for iteration/validation */
export const WORKSPACE_ROLES: readonly WorkspaceRole[] = [
  'Admin',
  'Editor',
  'Viewer',
] as const;

/** Roles that can be assigned to members */
export const ASSIGNABLE_ROLES: readonly WorkspaceRole[] = [
  'Admin',
  'Editor',
  'Viewer',
] as const;

/**
 * Check if role has admin privileges
 */
export const isAdminRole = (role: WorkspaceRole): boolean => role === 'Admin';

/**
 * Check if role can manage members (Admin role)
 * Note: Owners can also manage members - check isOwner separately
 */
export const canManageMembers = (role: WorkspaceRole): boolean => role === 'Admin';

/**
 * Check if user can manage workspace settings
 * Only workspace owners can manage settings
 */
export const canManageWorkspace = (isOwner: boolean): boolean => isOwner;

/**
 * Check if user can delete workspace
 * Only workspace owners can delete the workspace
 */
export const canDeleteWorkspace = (isOwner: boolean): boolean => isOwner;
